import * as z from 'zod'

import { MAX_CROSS_BREEDS, UNKNOWN_BREED_OPTION } from '@flows/configs/petHealthV2/constants'
import {
  NATIVE_TITLE_SIZE,
  v2InfoModal,
  v2Text,
  v2TextWithPetName,
} from '@flows/configs/petHealthV2/steps/elements/shared'
import { toBreedOptions } from '@flows/configs/petHealthV2/utils'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { ElementFragment } from '@flows/configs/shared/stepFragments'
import type { StepConfigParams } from '@flows/configs/types'
import type {
  AnyStepContentElement,
  SearchableListOption,
} from '@flows/core/types/stepContentTypes'
import { BaseSchema_PetHealthV2GetsafeDe } from '@flows/insurance/schemas'

const { breed_type, first_breed_name, second_breed_name, species } =
  BaseSchema_PetHealthV2GetsafeDe.shape

const breedOptions = (params: StepConfigParams, listPath: string) =>
  ({
    options: params.helperFunctions.getTransientPath(listPath),
    optionsTransformer: (breeds: readonly unknown[]) =>
      toBreedOptions(breeds.map(String), v2Text('steps.breeds.unknown').translationKey),
  }) as const

const breedSearch = (params: StepConfigParams) =>
  ({
    type: 'searchableList',
    shouldTrackValues: true,
    groupBy: 'alphabetical',
    searchPlaceholder: v2Text('steps.breeds.searchPlaceholder'),
    selectedIcon: 'PawPrint',
    pinnedOptions: [UNKNOWN_BREED_OPTION],
    options: params.helperFunctions.getTransientPath('breed_options'),
    optionsTransformer: (options: readonly SearchableListOption[]) =>
      options.map((option) =>
        option.value === UNKNOWN_BREED_OPTION ? { ...option, icon: 'SealQuestion' } : option
      ),
  }) as const

const breedsUnavailable = (params: StepConfigParams) =>
  ({
    type: 'value',
    valuePath: params.helperFunctions.getTransientPath('breeds_unavailable'),
    expectedValue: true,
  }) as const

/** The native app has no `alert` element, so the native breed steps show the notice as an info box. */
const breedsUnavailableNotice = (params: StepConfigParams) =>
  ({
    type: 'infoBox',
    showInfoIcon: true,
    contentConfiguration: [{ type: 'text', text: v2Text('steps.web_breeds.unavailable') }],
    condition: breedsUnavailable(params),
  }) as const

export const breedTypeElements = (params: StepConfigParams) =>
  ({
    elements: [
      {
        type: 'title',
        text: v2TextWithPetName(params, 'steps.breed_type.title'),
        size: NATIVE_TITLE_SIZE,
      },
      {
        type: 'choice',
        shouldTrackValues: true,
        variant: 'filled',
        name: params.helperFunctions.getParamPath('breed_type'),
        options: breed_type.options.map((value) => ({
          value,
          label: v2Text(`steps.breed_type.${value}`),
        })),
      },
    ],
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('breed_type')]: breed_type,
    }),
  }) as const satisfies ElementFragment

export const pureBreedElements = (params: StepConfigParams) =>
  ({
    elements: [
      { type: 'title', text: v2TextWithPetName(params, 'steps.pure_breed.title') },
      breedsUnavailableNotice(params),
      { ...breedSearch(params), name: params.helperFunctions.getParamPath('first_breed_name') },
    ],
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('first_breed_name')]: first_breed_name,
    }),
  }) as const satisfies ElementFragment

export const crossBreedElements = (params: StepConfigParams) =>
  ({
    elements: [
      { type: 'title', text: v2TextWithPetName(params, 'steps.cross_breed.title') },
      breedsUnavailableNotice(params),
      {
        ...breedSearch(params),
        name: params.helperFunctions.getTransientPath('cross_breeds'),
        selectionMode: 'multiple',
        maxSelections: MAX_CROSS_BREEDS,
        slotPlaceholderImagePath: params.helperFunctions.getTransientPath('unknown_breed_image'),
        repeatableValues: [UNKNOWN_BREED_OPTION],
      },
    ],
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getTransientPath('cross_breeds')]: z
        .array(first_breed_name)
        .min(1)
        .max(MAX_CROSS_BREEDS),
    }),
  }) as const satisfies ElementFragment

const speciesIs = (params: StepConfigParams, petType: (typeof species.options)[number]) =>
  ({
    type: 'value',
    valuePath: params.helperFunctions.getParamPath('species'),
    expectedValue: petType,
  }) as const

const speciesPicked = (params: StepConfigParams) =>
  ({
    type: 'conditions',
    comparisonType: 'anyOf',
    conditions: species.options.map((petType) => speciesIs(params, petType)),
  }) as const

const webBreedDropDowns = (params: StepConfigParams, petType: (typeof species.options)[number]) =>
  (['first', 'second'] as const).map((position) => ({
    type: 'dropDown',
    shouldTrackValues: true,
    styleType: 'underlined',
    useFilterInput: true,
    isOptional: position === 'second',
    name: params.helperFunctions.getParamPath(`${position}_breed_name`),
    label: v2Text(`steps.web_breeds.${position}`),
    ...(position === 'second' && { collapsedLabel: v2Text('steps.web_breeds.addSecond') }),
    condition: speciesIs(params, petType),
    ...breedOptions(params, `breeds_by_species.${petType}`),
  })) satisfies AnyStepContentElement[]

export const webBreedElements = (params: StepConfigParams) =>
  ({
    elements: [
      {
        type: 'title',
        text: v2TextWithPetName(params, 'steps.web_breeds.title'),
        infoButton: v2InfoModal('steps.web_breeds'),
        condition: speciesPicked(params),
      },
      {
        type: 'alert',
        variant: 'warning',
        message: v2Text('steps.web_breeds.unavailable'),
        condition: {
          type: 'conditions',
          comparisonType: 'allOf',
          conditions: [speciesPicked(params), breedsUnavailable(params)],
        },
      },
      ...species.options.flatMap((petType) => webBreedDropDowns(params, petType)),
    ],
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('first_breed_name')]: first_breed_name,
      [params.helperFunctions.getParamPath('second_breed_name')]: second_breed_name
        .nullable()
        .or(z.literal('')),
    }),
  }) as const satisfies ElementFragment
